/**
 * UserTemplateEditing Component
 *
 * Template management interface for user management email templates with:
 * - ButtonGroup interface for invitation and activation-reminder templates
 * - Rich text editor with preview functionality
 * - Template variable hints and validation
 * - Real-time preview with sample data
 * - Save/reset functionality with change tracking
 *
 * This component is adapted from TemplateEditing.tsx for User Management templates.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Save, RotateCcw, AlertCircle, KeyRound, ShieldOff, ShieldCheck, MailCheck, ShieldAlert, Shield } from 'lucide-react';

import {
  Input,
  TextArea,
  Card,
  CardHeader,
  CardBody,
  Select,
  SelectItem,
  Divider,
  Chip,
  Spinner,
  Alert,
  Button,
  useToastNotification
} from '@enfomentis/ui';

import { emailTemplateService } from '@/services/EmailTemplateService';
import { emailCategoryService } from '@/services/EmailCategoryService';
import { useAuth } from '@/hooks/useAuth';
import { Brand } from '@enfomentis/shared/config';
import type { EmailTemplate, EmailTemplateInsert, UserManagementTemplateType } from '@/types/email';

/**
 * Template editor data for a single template type
 */
interface TemplateEditorData {
  id: string | undefined;
  subject: string;
  content: string;
  hasChanges: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Available template variables for substitution in User Management templates
 */
const USER_TEMPLATE_VARIABLES = [
  { key: '{{recipient_name}}', description: 'Full name of the user (invitation, activation-reminder)' },
  { key: '{{user_name}}', description: 'Full name of the user (password-reset, account-disabled, account-reactivated, email-verification, password-changed)' },
  { key: '{{invitation_link}}', description: 'Complete activation URL link' },
  { key: '{{invitation_token}}', description: 'Raw activation token' },
  { key: '{{reset_link}}', description: 'Complete password reset URL link' },
  { key: '{{reset_token}}', description: 'Raw password reset token' },
  { key: '{{verification_link}}', description: 'Complete email verification URL link' },
  { key: '{{verification_token}}', description: 'Raw email verification token' },
  { key: '{{new_email}}', description: 'The new email address being verified' },
  { key: '{{current_email}}', description: 'The current email address being changed from' },
  { key: '{{expires_at}}', description: 'When the invitation/reset/verification expires' },
  { key: '{{sent_by}}', description: 'Admin who sent the invitation' },
  { key: '{{sent_date}}', description: 'Date the invitation/reset/verification was sent' },
  { key: '{{disabled_date}}', description: 'When the account was disabled' },
  { key: '{{disabled_by}}', description: 'Admin who disabled the account' },
  { key: '{{disable_reason}}', description: 'Reason for account disabling' },
  { key: '{{reactivated_date}}', description: 'When the account was reactivated' },
  { key: '{{reactivated_by}}', description: 'Admin who reactivated the account' },
  { key: '{{change_timestamp}}', description: 'When the password was changed (password-changed)' },
  { key: '{{change_method}}', description: 'How the password was changed (password-changed)' },
  { key: '{{support_email}}', description: 'Support contact email' },
  { key: '{{organization_name}}', description: 'Organization name' }
] as const;

/**
 * Get default template content for each User Management type
 * Uses Brand config for dynamic brand values
 * NOTE: invitation and activation-reminder templates have been moved to Beta Applications
 */
const getDefaultUserTemplates = (): Record<UserManagementTemplateType, { subject: string; content: string }> => ({
  'mfa-disabled': {
    subject: `MFA Disabled on Your ${Brand.displayName} Account 🔐`,
    content: `Hi {{user_name}},

This is a security notification to confirm that multi-factor authentication (MFA) has been disabled on your ${Brand.displayName} account.

**MFA Disabled Details:**
• Disabled on: {{disabled_date}}
• Your account now requires only your password for login

**If this was you:**
No action is needed. However, we strongly recommend keeping MFA enabled for better account security. You can re-enable MFA anytime in your account settings.

**If you did not make this change:**
Your account may be compromised. Please take immediate action:
1. Re-enable MFA immediately in your account settings
2. Change your password
3. Contact our support team at {{support_email}}
4. We will help you secure your account

**Security Best Practices:**
• Keep MFA enabled for maximum account protection
• Use a unique, strong password for ${Brand.displayName}
• Never share your password or MFA codes with anyone
• Review your active sessions regularly

**Re-enable MFA:**
To re-enable MFA for better security:
1. Log in to your ${Brand.displayName} account
2. Go to Settings → Security
3. Click "Enable Multi-Factor Authentication"
4. Follow the setup instructions

**Need Help?**
If you have any questions or concerns about this security change, please contact us at {{support_email}}.

Best regards,
The ${Brand.displayName} Team
{{support_email}}

---
This is an automated security notification sent on {{disabled_date}}.`
  },
  'password-changed': {
    subject: `Your ${Brand.displayName} Password Has Been Changed 🔐`,
    content: `Hi {{user_name}},

This is a security notification to confirm that your ${Brand.displayName} password was successfully changed.

**Change Details:**
• Changed on: {{change_timestamp}}
• Change method: {{change_method}}

**If this was you:**
No action is needed. Your account is secure and you can continue using ${Brand.displayName} with your new password.

**If you did not make this change:**
Your account may be compromised. Please take immediate action:
1. Contact our support team at {{support_email}}
2. We will help you secure your account

**Security Best Practices:**
• Use a unique, strong password for ${Brand.displayName}
• Never share your password with anyone
• Enable additional security features when available

**Need Help?**
If you have any questions or concerns about this password change, please contact us at {{support_email}}.

Best regards,
The ${Brand.displayName} Team
{{support_email}}

---
This is an automated security notification sent on {{change_timestamp}}.`
  },
  'email-verification': {
    subject: 'Verify Your New Email Address 📧',
    content: `Hi {{user_name}},

We received a request to change your email address for your ${Brand.displayName} account.

**Email Change Details:**
• Current email: {{current_email}}
• New email: {{new_email}}
• Requested on: {{sent_date}}

**Verify Your New Email:**
To complete the email address change, please click the secure verification link below:

{{verification_link}}

Or visit https://${Brand.domain}/verify-email and enter your verification code: {{verification_token}}

**Important Information:**
• This verification link expires on {{expires_at}}
• The link can only be used once
• Your current email will remain active until verification is complete
• If you didn't request this change, please ignore this email

**For Your Security:**
• Only use official ${Brand.displayName} links for email verification
• Never share this verification link with anyone
• If you suspect unauthorized access, contact support immediately

**Need Help?**
If you're having trouble verifying your email or have any questions, please contact our support team at {{support_email}}.

Once verified, you'll receive login confirmations and important account notifications at your new email address.

Best regards,
The ${Brand.displayName} Team
{{support_email}}

---
Email verification requested on {{sent_date}}. This is an automated security email.`
  },
  'password-reset': {
    subject: `Reset Your ${Brand.displayName} Password 🔐`,
    content: `Hi {{user_name}},

We received a request to reset your password for your ${Brand.displayName} account.

**Reset Your Password:**
Click the secure link below to create a new password:

{{reset_link}}

Or visit https://${Brand.domain}/password-reset and enter your reset code: {{reset_token}}

**Important Details:**
• This reset link expires on {{expires_at}}
• The link can only be used once
• If you didn't request this reset, please ignore this email
• Your account remains secure - no changes will be made without using this link

**Need Help?**
If you're having trouble resetting your password or have any security concerns, please contact our support team at ${Brand.supportEmail}.

**For Your Security:**
• Never share this reset link with anyone
• Only use official ${Brand.displayName} links for password resets
• If you suspect unauthorized access, contact support immediately

Best regards,
The ${Brand.displayName} Team
${Brand.supportEmail}

---
Password reset requested on {{sent_date}}. This is an automated security email.`
  },
  'account-disabled': {
    subject: `Important: Your ${Brand.displayName} Account Status Update`,
    content: `Dear {{user_name}},

We are writing to inform you that your ${Brand.displayName} account has been temporarily disabled.

**Account Status Details:**
• Account disabled on: {{disabled_date}}
• Reason: {{disable_reason}}
• Action taken by: {{disabled_by}}

**What This Means:**
You will not be able to access your trading journal or account features during this time. Any active sessions have been terminated for security purposes.

**Need Help?**
If you have questions about this account action or need assistance, please contact our support team:
• Email: {{support_email}}
• Subject: Account Disabled - {{user_name}}

We appreciate your understanding and are here to help resolve any concerns you may have.

Best regards,
The ${Brand.displayName} Team
{{support_email}}

---
This is an automated account notification sent on {{disabled_date}}.`
  },
  'account-reactivated': {
    subject: `Welcome Back! Your ${Brand.displayName} Account Has Been Restored 🎉`,
    content: `Dear {{user_name}},

Good news! Your ${Brand.displayName} account has been successfully reactivated and is now ready for use.

**Account Restoration Details:**
• Account reactivated on: {{reactivated_date}}
• Restored by: {{reactivated_by}}
• You can now access all your trading journal features

**Getting Back Started:**
✅ **Log in normally** - Use your existing email and password
✅ **Access your data** - All your previous trades and journal entries are preserved
✅ **Resume trading** - Continue where you left off with full platform access
✅ **Contact support** - We're here if you need any assistance

**Questions or Need Help?**
If you encounter any issues accessing your account or have questions about the reactivation:
• Email: {{support_email}}
• Subject: Account Reactivated - {{user_name}}

We apologize for any inconvenience during the account suspension and appreciate your patience. We're excited to have you back in the ${Brand.displayName} community!

Happy trading!

Best regards,
The ${Brand.displayName} Team
{{support_email}}

---
Account reactivated notification sent on {{reactivated_date}}.`
  }
});

/**
 * User Template Editing Component
 *
 * Features:
 * - ButtonGroup interface for invitation and activation-reminder templates
 * - Rich text editor with preview functionality
 * - Template variable hints and validation
 * - Real-time preview with sample data
 * - Save/reset functionality with change tracking
 * - Mobile-responsive design
 * - Error handling and loading states
 *
 * Usage:
 * ```tsx
 * <UserTemplateEditing />
 * ```
 */
export const UserTemplateEditing: React.FC = () => {
  // Auth context for admin username
  const { user } = useAuth();
  const adminUsername = user?.email || 'Unknown Admin';

  // State management
  const [selectedTab, setSelectedTab] = useState<UserManagementTemplateType>('password-reset');
  const [templates, setTemplates] = useState<Record<UserManagementTemplateType, TemplateEditorData>>({
    'password-reset': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'password-changed': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'account-disabled': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'account-reactivated': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'email-verification': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'mfa-disabled': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null }
  });
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [globalLoading, setGlobalLoading] = useState<boolean>(true);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Load templates on component mount
  const loadTemplates = useCallback(async (): Promise<void> => {
    try {
      setGlobalLoading(true);
      setGlobalError(null);

      const [passwordResetTemplate, passwordChangedTemplate, accountDisabledTemplate, accountReactivatedTemplate, emailVerificationTemplate, mfaDisabledTemplate] = await Promise.all([
        emailTemplateService.getUserManagementTemplate('password-reset'),
        emailTemplateService.getUserManagementTemplate('password-changed'),
        emailTemplateService.getUserManagementTemplate('account-disabled'),
        emailTemplateService.getUserManagementTemplate('account-reactivated'),
        emailTemplateService.getUserManagementTemplate('email-verification'),
        emailTemplateService.getUserManagementTemplate('mfa-disabled')
      ]);

      const defaults = getDefaultUserTemplates();
      setTemplates({
        'password-reset': {
          id: passwordResetTemplate?.id,
          subject: passwordResetTemplate?.subject || defaults['password-reset'].subject,
          content: passwordResetTemplate?.content || defaults['password-reset'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'password-changed': {
          id: passwordChangedTemplate?.id,
          subject: passwordChangedTemplate?.subject || defaults['password-changed'].subject,
          content: passwordChangedTemplate?.content || defaults['password-changed'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'account-disabled': {
          id: accountDisabledTemplate?.id,
          subject: accountDisabledTemplate?.subject || defaults['account-disabled'].subject,
          content: accountDisabledTemplate?.content || defaults['account-disabled'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'account-reactivated': {
          id: accountReactivatedTemplate?.id,
          subject: accountReactivatedTemplate?.subject || defaults['account-reactivated'].subject,
          content: accountReactivatedTemplate?.content || defaults['account-reactivated'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'email-verification': {
          id: emailVerificationTemplate?.id,
          subject: emailVerificationTemplate?.subject || defaults['email-verification'].subject,
          content: emailVerificationTemplate?.content || defaults['email-verification'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'mfa-disabled': {
          id: mfaDisabledTemplate?.id,
          subject: mfaDisabledTemplate?.subject || defaults['mfa-disabled'].subject,
          content: mfaDisabledTemplate?.content || defaults['mfa-disabled'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        }
      });
    } catch (err) {
      console.error('Failed to load user management email templates:', err);
      setGlobalError('Failed to load email templates. Please try again.');
    } finally {
      setGlobalLoading(false);
    }
  }, []);

  // Load templates when component mounts
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Handle template field changes
  const handleTemplateChange = (
    type: UserManagementTemplateType,
    field: 'subject' | 'content',
    value: string
  ): void => {
    setTemplates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
        hasChanges: true
      }
    }));
  };

  // Handle save template
  const handleSaveTemplate = async (type: UserManagementTemplateType): Promise<void> => {
    const template = templates[type];
    if (!template.hasChanges) return;

    try {
      setTemplates(prev => ({
        ...prev,
        [type]: { ...prev[type], isLoading: true, error: null }
      }));

      if (!adminUsername || adminUsername === 'Unknown Admin') {
        throw new Error('Admin user not authenticated');
      }

      // Ensure User Management category exists
      const category = await emailCategoryService.ensureCategoryExists(
        'User Management',
        'User invitation, activation, and account management emails'
      );

      const templateData = {
        categoryId: category.id,
        templateType: type as string,
        subject: template.subject.trim(),
        content: template.content.trim(),
        signature: null,
        updatedBy: adminUsername
      };

      let savedTemplate: EmailTemplate;
      if (template.id) {
        // Update existing template
        savedTemplate = await emailTemplateService.update(template.id, templateData);
      } else {
        // Create new template
        savedTemplate = await emailTemplateService.add(templateData as EmailTemplateInsert);
      }

      setTemplates(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          id: savedTemplate.id,
          hasChanges: false,
          isLoading: false,
          error: null
        }
      }));

      showToast({
        color: 'success',
        title: 'Template Saved',
        description: `${type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')} template saved successfully.`
      });
    } catch (err) {
      console.error(`Failed to save ${type} template:`, err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save template';

      setTemplates(prev => ({
        ...prev,
        [type]: { ...prev[type], isLoading: false, error: errorMessage }
      }));

      showToast({
        color: 'danger',
        title: 'Save Failed',
        description: `Failed to save ${type.replace('-', ' ')} template. Please try again.`
      });
    }
  };

  // Handle reset template to default
  const handleResetTemplate = (type: UserManagementTemplateType): void => {
    const defaults = getDefaultUserTemplates();
    setTemplates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        subject: defaults[type].subject,
        content: defaults[type].content,
        hasChanges: true,
        error: null
      }
    }));
  };

  // Render template variables helper
  const renderVariableHints = (): React.ReactNode => {
    return (
      <Card shadow="sm">
        <CardHeader>
          <h4 className="text-sm font-medium text-text-primary">Available Variables</h4>
        </CardHeader>
        <CardBody className="space-y-2">
          {USER_TEMPLATE_VARIABLES.map((variable) => (
            <div key={variable.key} className="flex items-center justify-between">
              <code className="text-xs bg-default-100 px-2 py-1 rounded">
                {variable.key}
              </code>
              <p className="text-xs text-text-secondary ml-2">{variable.description}</p>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  // Render template preview with sample data
  const renderPreview = (type: UserManagementTemplateType): React.ReactNode => {
    const template = templates[type];
    const sampleData = {
      '{{recipient_name}}': 'John Doe',
      '{{user_name}}': 'John Doe',
      '{{invitation_link}}': `https://${Brand.domain}/activate/abc123def456`,
      '{{invitation_token}}': 'abc123def456',
      '{{reset_link}}': `https://${Brand.domain}/password-reset?token=reset_abc123def456`,
      '{{reset_token}}': 'reset_abc123def456',
      '{{verification_link}}': `https://${Brand.domain}/verify-email/verify_abc123def456`,
      '{{verification_token}}': 'verify_abc123def456',
      '{{new_email}}': 'john.doe.new@example.com',
      '{{current_email}}': 'john.doe@example.com',
      '{{expires_at}}': 'December 27, 2024 at 11:59 PM',
      '{{sent_by}}': 'Christopher',
      '{{sent_date}}': 'December 24, 2024',
      '{{disabled_date}}': 'December 24, 2024 at 2:30 PM',
      '{{disabled_by}}': 'Christopher (Admin)',
      '{{disable_reason}}': 'Policy Violation',
      '{{reactivated_date}}': 'December 25, 2024 at 10:15 AM',
      '{{reactivated_by}}': 'Christopher (Admin)',
      '{{change_timestamp}}': 'October 13, 2025 at 3:45 PM',
      '{{change_method}}': 'Authenticated password change',
      '{{support_email}}': Brand.supportEmail,
      '{{organization_name}}': Brand.displayName
    };

    // Replace variables with sample data
    let previewSubject = template.subject;
    let previewContent = template.content;

    Object.entries(sampleData).forEach(([variable, value]) => {
      previewSubject = previewSubject.replace(new RegExp(variable, 'g'), value);
      previewContent = previewContent.replace(new RegExp(variable, 'g'), value);
    });

    return (
      <Card shadow="sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-primary" />
            <h4 className="text-sm font-medium text-text-primary">Email Preview</h4>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Subject:</p>
            <p className="text-sm font-medium text-text-primary">{previewSubject}</p>
          </div>
          <Divider />
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Content:</p>
            <div className="bg-default-50 p-3 rounded-md">
              <pre className="text-xs text-text-primary whitespace-pre-wrap font-sans">
                {previewContent}
              </pre>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  // Render template editor
  const renderTemplateEditor = (type: UserManagementTemplateType): React.ReactNode => {
    const template = templates[type];

    return (
      <div className="space-y-4">
        {/* Template form */}
        <div className="space-y-4">
          <Input
            label="Email Subject"
            value={template.subject}
            onValueChange={(value) => handleTemplateChange(type, 'subject', value)}
            placeholder="Enter email subject line..."
            description="Subject line for the user email notification"
            isRequired
          />
          <TextArea
            label="Email Content"
            value={template.content}
            onValueChange={(value) => handleTemplateChange(type, 'content', value)}
            placeholder="Enter email content..."
            description="Email body content. Use variables like {{recipient_name}} for dynamic content."
            minRows={8}
            maxRows={12}
            isRequired
          />
        </div>

        {/* Template error */}
        {template.error && (
          <Alert color="danger" variant="flat">
            <AlertCircle size={16} />
            {template.error}
          </Alert>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            color="primary"
            variant="solid"
            size="sm"
            startContent={<Save size={16} />}
            onPress={() => handleSaveTemplate(type)}
            isLoading={template.isLoading}
            isDisabled={!template.hasChanges || template.isLoading}
          >
            Save Template
          </Button>
          <Button
            color="secondary"
            variant="flat"
            size="sm"
            startContent={<RotateCcw size={16} />}
            onPress={() => handleResetTemplate(type)}
            isDisabled={template.isLoading}
          >
            Reset to Default
          </Button>
          {template.hasChanges && (
            <Chip color="warning" variant="flat" size="sm">
              Unsaved Changes
            </Chip>
          )}
        </div>

        <Divider />

        {/* Preview toggle */}
        <div className="flex items-center gap-2">
          <Button
            color={isPreviewMode ? 'primary' : 'default'}
            variant={isPreviewMode ? 'solid' : 'flat'}
            size="sm"
            startContent={<Eye size={16} />}
            onPress={() => setIsPreviewMode(!isPreviewMode)}
          >
            {isPreviewMode ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>

        {/* Preview and variable hints */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isPreviewMode && (
            <div className="lg:col-span-1">
              {renderPreview(type)}
            </div>
          )}
          <div className={isPreviewMode ? 'lg:col-span-1' : 'lg:col-span-2'}>
            {renderVariableHints()}
          </div>
        </div>
      </div>
    );
  };

  // Check if any template has unsaved changes
  const hasUnsavedChanges = Object.values(templates).some(template => template.hasChanges);

  if (globalLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner color="primary" />
        <span className="ml-3 text-text-secondary">Loading templates...</span>
      </div>
    );
  }

  if (globalError) {
    return (
      <div className="text-center py-12">
        <Alert color="danger" variant="flat" className="mb-4">
          <AlertCircle size={16} />
          {globalError}
        </Alert>
        <Button
          color="primary"
          variant="flat"
          onPress={loadTemplates}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Template options for Select component
  const templateOptions = [
    { key: 'password-reset', label: 'Password Reset', icon: KeyRound },
    { key: 'password-changed', label: 'Password Changed', icon: ShieldAlert },
    { key: 'mfa-disabled', label: 'MFA Disabled', icon: Shield },
    { key: 'account-disabled', label: 'Account Disabled', icon: ShieldOff },
    { key: 'account-reactivated', label: 'Account Reactivated', icon: ShieldCheck },
    { key: 'email-verification', label: 'Email Verification', icon: MailCheck }
  ];

  return (
    <Card shadow="none" radius="none" fullWidth className="h-full flex flex-col">
      {/* Header with Template Type Selection */}
      <CardHeader className="flex-shrink-0 border-b border-default-200">
        <div className="w-full space-y-4">
          <div className="flex items-center gap-4">
            <Select
              label="Email Template"
              placeholder="Select a template"
              selectedKeys={[selectedTab]}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string;
                if (value && typeof value === 'string') {
                  setSelectedTab(value as UserManagementTemplateType);
                }
              }}
              variant="bordered"
              size="md"
              className="max-w-xs"
              startContent={(() => {
                const selectedOption = templateOptions.find(opt => opt.key === selectedTab);
                return selectedOption?.icon
                  ? React.createElement(selectedOption.icon, { size: 18, className: "text-default-400" })
                  : undefined;
              })()}
            >
              {templateOptions.map((option) => (
                <SelectItem
                  key={option.key}
                  startContent={<option.icon size={18} className="text-default-400" />}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
            {hasUnsavedChanges && (
              <Chip color="warning" variant="flat" size="sm">
                Unsaved Changes
              </Chip>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Scrollable Content */}
      <CardBody className="flex-1 overflow-y-auto">
        {renderTemplateEditor(selectedTab)}
      </CardBody>
    </Card>
  );
};